import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Switch } from '@ui/components/ui/switch';
import { Textarea } from '@ui/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/components/ui/select';
import { useDisconnectSource, usePhillConnections } from '@data/hooks/auth';
import {
  useAiSummaryInstructions,
  useSetAiSummaryInstructions,
  useSetReminderTime,
  useOwnMembership,
} from '@data/hooks/membership';
import { useSendReportNudgeToSelf } from '@data/hooks/organization';
import { useStore } from '@data/storages';
import { UserEntity } from '@data/utils/types';
import Connections from '@app/components/core/philBot/Connections';
import { resetOnboarding } from '@app/components/core/philBot/onboardingState';
import { useConnectSource } from '@app/components/core/philBot/useConnectSource';
import { Roles } from '@app/utils/types';

// The reminder job runs at half past every hour, so these are the only times a reminder can land.
const REMINDER_TIMES = ['12:30', '13:30', '14:30', '15:30', '16:30', '17:30', '18:30', '19:30', '20:30'];
const DEFAULT_REMINDER_TIME = '17:30';
const WORKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;

// Matches the cap the API validates against, so the box stops accepting what the save would refuse.
const MAX_AI_SUMMARY_INSTRUCTIONS = 500;

const ReminderTimeSelect = ({
  value,
  onChange,
  disabled,
  label,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  label: string;
  className?: string;
}): JSX.Element => (
  <Select value={value} onValueChange={next => next && onChange(next)} disabled={disabled}>
    <SelectTrigger className={cx('shrink-0 text-foreground tabular-nums', className)} aria-label={label}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {REMINDER_TIMES.map(time => (
        <SelectItem key={time} value={time} className="tabular-nums">
          {time}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// Settings → Phill bot (employee only): when Phill messages you, and what it may read when it drafts.
// Failures surface through the app's global error toast, so nothing here renders its own error line.
const PhilBotSection = ({ user }: { user: UserEntity }): JSX.Element => {
  const { t } = useTranslation();
  const { organization } = useStore();
  // Your own membership; see useOwnMembership for why the store's list is a colleague's.
  const { membership } = useOwnMembership(organization?.id, user.id);
  // A dev build is where anyone tests, and an admin is who tests a deployed one. Neither is the
  // customer the block below is being kept away from.
  const canTestPhill = import.meta.env.DEV || organization?.role === Roles.admin;
  const [reminderTime, setReminderTime] = useState(membership?.reminderTime ?? DEFAULT_REMINDER_TIME);
  const [timesByDay, setTimesByDay] = useState(membership?.reminderTimesByDay ?? null);
  const storedReminderTime = membership?.reminderTime;
  // Joined so the effect re-runs on a changed day, not on every new array the read hands back.
  const storedTimesByDay = membership?.reminderTimesByDay?.join(',');
  useEffect(() => {
    if (!membership?.id) return;
    setReminderTime(storedReminderTime ?? DEFAULT_REMINDER_TIME);
    setTimesByDay(storedTimesByDay ? storedTimesByDay.split(',') : null);
  }, [membership?.id, storedReminderTime, storedTimesByDay]);
  const { mutate: saveReminderTime } = useSetReminderTime(membership?.id);

  const changeReminder = (nextTime: string, nextByDay: string[] | null): void => {
    const previous = { reminderTime, timesByDay };
    setReminderTime(nextTime);
    setTimesByDay(nextByDay);
    saveReminderTime(
      { reminderTime: nextTime, reminderTimesByDay: nextByDay },
      {
        onError: () => {
          setReminderTime(previous.reminderTime);
          setTimesByDay(previous.timesByDay);
        },
      },
    );
  };

  const changeDayTime = (day: number, value: string): void => {
    if (!timesByDay) return;
    changeReminder(
      reminderTime,
      timesByDay.map((time, index) => (index === day ? value : time)),
    );
  };

  const { data: storedInstructions, isSuccess: instructionsRead } = useAiSummaryInstructions(membership?.id);
  const [instructions, setInstructions] = useState('');
  const savedInstructions = storedInstructions?.aiSummaryInstructions;
  // Seeded once per membership, after the read lands: a later change would replace what is being typed.
  const seededFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!instructionsRead || !membership?.id || seededFor.current === membership.id) return;

    seededFor.current = membership.id;
    setInstructions(savedInstructions ?? '');
  }, [instructionsRead, membership?.id, savedInstructions]);

  const { data: connections } = usePhillConnections();
  const { mutate: disconnect, variables: disconnecting, isPending: isDisconnecting } = useDisconnectSource();
  const { mutate: saveInstructions } = useSetAiSummaryInstructions(membership?.id);
  const {
    mutate: sendNudge,
    isPending: isSendingNudge,
    isSuccess: hasSentNudge,
  } = useSendReportNudgeToSelf(organization?.id as string);

  // Shows the welcome again on the next load, so the flow can be walked without a fresh account.
  const replayOnboarding = (): void => {
    resetOnboarding(user.id);
    window.location.reload();
  };

  // Saved on blur, not per keystroke: nothing on the page depends on it.
  const commitInstructions = (): void => {
    const trimmed = instructions.trim();
    if (trimmed === (savedInstructions ?? '')) return;

    saveInstructions(trimmed, { onError: () => setInstructions(savedInstructions ?? '') });
  };

  const connect = useConnectSource();

  return (
    <section>
      <p className="text-xs text-muted-foreground">{t('philBot.body')}</p>

      <div className="mt-4 rounded-lg border border-border p-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{t('philBot.reminderTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('philBot.reminderHint')}</p>
          </div>
          <ReminderTimeSelect
            value={reminderTime}
            onChange={next => changeReminder(next, null)}
            disabled={!membership?.id || Boolean(timesByDay)}
            label={t('philBot.reminderTitle')}
          />
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-border pt-2.5">
          <label htmlFor="reminder-per-day" className="text-xs text-muted-foreground">
            {t('philBot.reminderPerDay')}
          </label>
          <Switch
            id="reminder-per-day"
            checked={Boolean(timesByDay)}
            onCheckedChange={on => changeReminder(reminderTime, on ? WORKDAYS.map(() => reminderTime) : null)}
            disabled={!membership?.id}
            aria-label={t('philBot.reminderPerDay')}
          />
        </div>

        <div
          className={cx(
            'grid transition-[grid-template-rows] duration-300 ease-out',
            timesByDay ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="-mx-1 overflow-hidden px-1">
            <div className="grid grid-cols-3 gap-1.5 pt-2.5 sm:grid-cols-5">
              {WORKDAYS.map((day, index) => (
                <div key={day} className="grid min-w-0 gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(`philBot.reminderDays.${day}`)}
                  </span>
                  <ReminderTimeSelect
                    value={timesByDay?.[index] ?? reminderTime}
                    onChange={next => changeDayTime(index, next)}
                    disabled={!timesByDay}
                    label={t(`philBot.reminderDays.${day}`)}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="pt-2">
        <label htmlFor="ai-summary-instructions" className="text-xs font-medium text-foreground">
          {t('philBot.instructionsTitle')}
        </label>
        {/* `min-h`, not `rows`: the textarea sizes to its content, so `rows` sets only the start. */}
        <Textarea
          id="ai-summary-instructions"
          value={instructions}
          onChange={event => setInstructions(event.target.value)}
          onBlur={commitInstructions}
          disabled={!membership?.id || !instructionsRead}
          maxLength={MAX_AI_SUMMARY_INSTRUCTIONS}
          placeholder={t('philBot.instructionsPlaceholder')}
          className="mt-1 min-h-24 text-xs"
        />
        <p className="mt-1 text-xs text-muted-foreground">{t('philBot.instructionsHint')}</p>
      </div>

      <div className="mt-5">
        <p className="mb-2 text-sm font-medium text-foreground">{t('philBot.connectedApps')}</p>
        <Connections
          connections={connections}
          onConnect={connect}
          onDisconnect={disconnect}
          pending={isDisconnecting ? (disconnecting ?? null) : null}
        />
      </div>
      {/* Two ways to see whether it actually works, and neither belongs in front of a customer: one
          replays the welcome, the other makes Phill message you now rather than at the end of the day.
          Both message nobody but the person pressing them — the org-wide nudge is a separate endpoint
          with no control anywhere, deliberately, because it DMs every colleague at once. */}
      {canTestPhill && (
        <div className="mt-6 space-y-2 border-t border-border pt-4">
          {/* Local only: it reloads the page to replay the welcome, which is a debugging move rather
              than something to hand an admin on a live organization. */}
          {import.meta.env.DEV && (
            <button
              type="button"
              onClick={replayOnboarding}
              className="block text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('philBot.replayOnboarding')}
            </button>
          )}
          <button
            type="button"
            onClick={() => sendNudge()}
            disabled={isSendingNudge || !organization?.id}
            className="block text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            {isSendingNudge ? t('philBot.sendingReportForm') : t('philBot.sendReportForm')}
          </button>
          {hasSentNudge && <p className="text-xs text-muted-foreground">{t('philBot.reportFormSent')}</p>}
        </div>
      )}
    </section>
  );
};

export default PhilBotSection;
